import type { ApplicationConfig} from '@angular/core';
import { provideZoneChangeDetection, provideAppInitializer, inject } from '@angular/core';
import { provideRouter, withInMemoryScrolling, withViewTransitions, withComponentInputBinding } from '@angular/router';

import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideNativeDateAdapter } from '@angular/material/core';
import { authRoutes } from './core/auth/auth.routes';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { TokenInterceptor } from './interceptors/token-interceptor';
import { LoaderInterceptor } from './interceptors/loader-interceptor';
import { AuthService } from './core/services/auth.service';
import { UserManagementService } from './core/services/user-management.service';
import { firstValueFrom } from 'rxjs';

/**
 * Application Initializer
 * Runs before the app starts to establish authentication state
 *
 * Flow:
 * 1. Check for valid Cognito session
 * 2. If valid, fetch user details from backend
 * 3. Store user data in AuthService
 * 4. Let router handle navigation via guards
 */
function initializeApp(): Promise<void> {
  const authService = inject(AuthService);
  const userService = inject(UserManagementService);

  return new Promise<void>((resolve) => {
    // Try to get user session from Cognito
    firstValueFrom(authService.getUserBySession())
      .then((session) => {
        if (!session?.payload?.sub) {
          // No valid session - let router handle redirect via authGuard
          resolve();
          return;
        }

        // Valid session found, fetch user details from backend
        // Remove @astrazeneca.net suffix from prid if present
        const rawPrid = session.payload.prid;
        const prid = rawPrid?.replace('@astrazeneca.net', '') ?? rawPrid;
        firstValueFrom(userService.getUserByPRID(prid))
          .then((user) => {
            if (user) {
              // Store user data in AuthService
              authService.setUserData(user);

              // Update last login timestamp in background (don't block app initialization)
              firstValueFrom(userService.updateLastLogin(prid))
                .catch(() => {
                  // Silently fail - last login update is not critical for app functionality
                  console.warn('Failed to update last login timestamp');
                });
            }
            resolve();
          })
          .catch(() => {
            // User not found in backend, but session is valid
            // Let the app continue, dashboard will handle this
            resolve();
          });
      })
      .catch(() => {
        // Session fetch failed - let router handle redirect via authGuard
        resolve();
      });
  });
}

/**
 * Application Configuration
 *
 * Router Configuration:
 * - authRoutes: Authentication flow (login, callback, logout)
 * - routes: Main application routes (protected by guards)
 *
 * Router Features:
 * - withComponentInputBinding: Automatically bind route params to @Input() properties
 * - withViewTransitions: Enable smooth page transitions
 * - withInMemoryScrolling: Restore scroll position on navigation
 *
 * HTTP Interceptors:
 * - LoaderInterceptor: Shows loading indicator for HTTP requests
 * - TokenInterceptor: Adds authentication token to outgoing requests
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      [...authRoutes, ...routes], // Auth routes must come first to avoid conflicts
      withComponentInputBinding(),
      withViewTransitions(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled'
      })
    ),
    provideAnimationsAsync(),
    provideNativeDateAdapter(),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    provideAppInitializer(initializeApp),
  ],
};
